import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from 'react'
import {
  DayDataTypes,
  apiGetSeasonManualData,
  ApiGetSeasonManualDataPostData,
  EntityType,
  apiSaveData,
  ApiSaveDataDateDataList
} from 'src/api'
import { useAbortController, useAppSelector } from 'src/hooks'
import { selectUser } from 'src/redux'
import { dateUtils } from 'src/utils/date.util'
import {
  RowLoaderControllerMap,
  CapacityRow,
  ChannelRow,
  LoadedCapacityRows,
  RateRow,
  RatesDataGridContextAction,
  RatesDataGridContextActionTypes,
  RatesDataGridContextState,
  RowType,
  SkeletonRow,
  LoadedChannelRows
} from './RatesDataGridContext.types'
import { TreeItemI } from 'src/components/customTree/CustomTree'
import { hotelDataUtils } from 'src/utils/api/hotelDataUtils'
import { customTreeUtils } from 'src/components'
import { RatesDataGridContextUtils } from './RatesDataGridContext.utils'
import { generateUUID } from 'src/utils/generateUid'
import { t } from 'i18next'
import {
  DEFAULT_LOAD_DAYS_QTY,
  RATE_DAY_VALUE_CONFIRM_LIMIT
} from 'src/applications/rates/Rates.constants'
import { useToastContext } from 'src/contexts/Toast.context'
import { useRatesContext } from 'src/applications/rates/Rates.context'

interface RatesDataGridContextProps extends RatesDataGridContextState {
  refreshData: () => Promise<void>
  changeRateRowDataType: ({
    row,
    dataType,
    index
  }: {
    row: RateRow | CapacityRow | ChannelRow
    dataType: keyof DayDataTypes
    index: number
  }) => void
  openRateCapacityRows: ({
    row,
    rowIndex
  }: {
    row: RateRow
    rowIndex: number
  }) => Promise<void>
  closeRateCapacityRows: ({
    row,
    index
  }: {
    row: RateRow
    index: number
  }) => void
  openRateChannelRows: ({
    index
  }: {
    index: number
    row: RateRow | CapacityRow
  }) => void
  closeRateChannelRows: ({
    index
  }: {
    index: number
    row: RateRow | CapacityRow
  }) => void
  changeFilterVisibility: (id: string) => void
  changeTreeItems: (treeItems: TreeItemI[]) => void
  changeFilterRateId: (rateId: string) => void
  changeDateRange: (newDateRange: [Date, Date]) => void
  resetFilters: () => void
  setEditDateData: (params: {
    date: Date
    dayData: Partial<DayDataTypes>
    row: RateRow | ChannelRow | CapacityRow
  }) => void
  openMassiveModModal: (row?: RateRow | ChannelRow | CapacityRow) => void
  closeMassiveModModal: () => void
  saveEditedData: () => Promise<void>
  capacityLoaderControllers: React.MutableRefObject<RowLoaderControllerMap>
  channelLoaderControllers: React.MutableRefObject<RowLoaderControllerMap>
}

const RatesDataGridContext = createContext<RatesDataGridContextProps>({} as any)

const reducer = (
  state: RatesDataGridContextState,
  { type, payload }: RatesDataGridContextAction
): RatesDataGridContextState => {
  switch (type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: payload
      }
    case 'SET_INITIAL_STATE': {
      const { apiData } = payload
      const newRows = apiData
        ? RatesDataGridContextUtils.hotelDataListToRows({
            hotelDataList: apiData.hotelDataList
          })
        : []
      return {
        ...state,
        rows: newRows,
        ...payload
      }
    }
    case 'UPDATE_API_DATA': {
      return {
        ...state,
        ...payload
      }
    }
    case 'SET_DATE_RANGE': {
      return { ...state, dateRange: payload }
    }
    case 'SET_ROWS': {
      return {
        ...state,
        rows: payload
      }
    }
    case 'CHANGE_RATE_ROW_DATA_TYPE': {
      const { row, dataType } = payload
      const newEditedDateList = state.editedDaysList
      return {
        ...state,
        rows: state.rows.map((currentRow) => {
          if (
            (currentRow.type === RowType.CAPACITY ||
              currentRow.type === RowType.RATE ||
              currentRow.type === RowType.CHANNEL) &&
            currentRow.data.roomTypeId === row.data.roomTypeId
          ) {
            if (newEditedDateList[currentRow.id])
              delete newEditedDateList[currentRow.id]
            return {
              ...currentRow,
              dayDataType: dataType
            } as RateRow
          }
          return currentRow
        })
      }
    }
    case 'OPEN_RATE_ROW': {
      const rowToOpen = state.rows[payload.rowIndex] as RateRow
      const { rowIndex, type } = payload
      const openedRow = { ...rowToOpen }
      if (type === RowType.CAPACITY) {
        openedRow.showCapacities = true
      }
      if (type === RowType.CHANNEL) {
        openedRow.showChannels = true
      }
      return {
        ...state,
        rows: [
          ...state.rows.slice(0, rowIndex),
          openedRow,
          ...state.rows.slice(rowIndex + 1)
        ]
      }
    }
    case 'CLOSE_RATE_ROW': {
      const { index, type } = payload
      const rowToClose = state.rows[index] as RateRow
      const closedRow = { ...rowToClose }
      if (type === RowType.CAPACITY) {
        closedRow.showCapacities = false
      }
      if (type === RowType.CHANNEL) {
        closedRow.showChannels = false
      }
      const closedFatherIds = [rowToClose.id]
      const newEditedDataList = state.editedDaysList

      return {
        ...state,
        rows: [
          ...state.rows.slice(0, index),
          closedRow,
          ...state.rows.slice(index + 1).map((row) => {
            if (
              closedFatherIds.some(
                (id) => 'fatherId' in row && id === row.fatherId
              ) &&
              row.type === type
            ) {
              if (newEditedDataList[row.id]) delete newEditedDataList[row.id]
              closedFatherIds.push(row.id)
            }
            return row
          })
        ]
      }
    }
    case 'UPDATE_ROWS': {
      const { rows, rowsReplaceQty = 0, isCapacityRows } = payload
      let rowIndex = payload.rowIndex
      if (isCapacityRows) {
        while (
          state.rows[rowIndex].type === RowType.CHANNEL ||
          state.rows[rowIndex].type === RowType.CHANNEL_SKELETON
        ) {
          rowIndex++
        }
      }
      return {
        ...state,
        rows: [
          ...state.rows.slice(0, rowIndex),
          ...rows,
          ...state.rows.slice(rowIndex + rowsReplaceQty)
        ]
      }
    }
    case 'ADD_ROWS': {
      const { index, rows } = payload
      return {
        ...state,
        rows: [
          ...state.rows.slice(0, index + 1),
          ...rows,
          ...state.rows.slice(index + 1)
        ]
      }
    }
    case 'REMOVE_ROWS': {
      const { rowIndex, rowsToRemove } = payload
      return {
        ...state,
        rows: [
          ...state.rows.slice(0, rowIndex),
          ...state.rows.slice(rowIndex + rowsToRemove)
        ]
      }
    }
    case 'SET_TREE_ITEMS':
      return {
        ...state,
        treeItems: payload
      }
    case 'RESET_FILTERS': {
      return {
        ...state,
        filterVisibilityId: '',
        treeItems: customTreeUtils.checkAll(state.treeItems, true),
        filterRateId: ''
      }
    }
    case 'SET_FILTER_RATE_ID':
      return { ...state, filterRateId: payload }
    case 'SET_FILTER_VISIBILITY_ID':
      return { ...state, filterVisibilityId: payload }
    case 'SET_EDITED_DATE': {
      const { row, date, dayData } = payload
      const isOverLimit =
        dayData.price && dayData.price > RATE_DAY_VALUE_CONFIRM_LIMIT
      return {
        ...state,
        editedDaysList: {
          ...state.editedDaysList,
          [row.id]: {
            ...state.editedDaysList[row.id],
            [dateUtils.formatDateToString(date)]: dayData
          }
        },
        ...(isOverLimit && {
          editedDaysListOverLimit: [
            ...state.editedDaysListOverLimit,
            {
              date,
              row
            }
          ]
        })
      }
    }
    case 'EMPTY_EDITED_DATE': {
      return { ...state, editedDaysList: {}, editedDaysListOverLimit: [] }
    }
    case 'SET_MASSIVE_MOD_MODAL': {
      return { ...state, massiveModModalOpen: payload }
    }

    default:
      return getInitialState()
  }
}

const getInitialState = (): RatesDataGridContextState => ({
  dateRange: [
    new Date(),
    dateUtils.addToDate(new Date(), {
      value: DEFAULT_LOAD_DAYS_QTY - 1,
      type: 'days'
    })
  ],
  daysList: dateUtils.getDateList(
    new Date(),
    dateUtils.addToDate(new Date(), {
      value: DEFAULT_LOAD_DAYS_QTY - 1,
      type: 'days'
    })
  ),
  filterRateId: '',
  loading: true,
  rows: [],
  treeItems: [],
  filterVisibilityId: '',
  apiData: undefined,
  editedDaysList: {},
  editedDaysListOverLimit: [],
  massiveModModalOpen: false
})

const RatesDataGridContextProvider: ReactFC = ({ children }) => {
  const toast = useToastContext()
  const { environmentId } = useRatesContext()

  // Capacities map
  const loadedCapacities = useRef<LoadedCapacityRows>({})
  const capacityLoaderControllers = useRef<RowLoaderControllerMap>({})

  // Channels map
  const loadedChannels = useRef<LoadedChannelRows>({})
  const channelLoaderControllers = useRef<RowLoaderControllerMap>({})

  // reducer
  const [state, dispatch] = useReducer(reducer, getInitialState())
  const {
    dateRange,
    filterRateId,
    rows,
    treeItems,
    filterVisibilityId,
    apiData,
    editedDaysList
  } = state

  // redux states
  const { serverUrl, token } = useAppSelector(selectUser)

  // custom hooks
  const { controller: fetchDataController, abortAndReplace } =
    useAbortController()

  // localHooks
  const isInitialCall = useRef(true)
  const isInitialFilterCall = useRef(true)

  const fetchData = useCallback(
    async (
      params: Partial<ApiGetSeasonManualDataPostData> & {
        controller?: AbortController
      } = {}
    ) => {
      const { controller: paramsController, ...postData } = params
      const res = await apiGetSeasonManualData({
        serverPath: serverUrl,
        data: {
          userToken: token,
          iniDate: dateUtils.formatDateToString(dateRange[0]),
          endDate: dateUtils.formatDateToString(dateRange[1]),
          environmentDataId: environmentId,
          filterVisibilityId,
          ...postData
        },
        controller: paramsController ?? fetchDataController
      })

      return res.data
    },
    [
      serverUrl,
      token,
      dateRange,
      filterVisibilityId,
      environmentId,
      fetchDataController
    ]
  )

  const updateData = useCallback(
    async ({ abortController }: { abortController: AbortController }) => {
      try {
        dispatch({
          type: 'SET_LOADING',
          payload: true
        })
        const checkedItems = customTreeUtils.getCheckedItems(treeItems)
        let checkedHotelIds: string[] | undefined
        let checkedRoomTypeIds: string[] | undefined

        checkedItems.forEach((treeItem) => {
          const { type } = treeItem

          if (type === EntityType.HotelAuxList) {
            checkedHotelIds
              ? checkedHotelIds.push(treeItem.id)
              : (checkedHotelIds = [treeItem.id])
            return
          }

          if (type === EntityType.RoomTypeAuxList) {
            checkedRoomTypeIds
              ? checkedRoomTypeIds.push(treeItem.id)
              : (checkedRoomTypeIds = [treeItem.id])
          }
        })

        const loadedChildrenCapacityIds: Set<string> = new Set()
        const childrenCapacityHotelIds: Set<string> = new Set()
        const childrenCapacityRoomTypeIds: Set<string> = new Set()
        const childrenCapacityRateIds: Set<string> = new Set()

        const childrenChannelHotelIds: Set<string> = new Set()
        const childrenChannelRoomTypeIds: Set<string> = new Set()
        const childrenChannelRateIds: Set<string> = new Set()
        const childrenChannelCapacityIds: Set<string> = new Set()

        const capacityOpenedRows: string[] = []
        const channelOpenedRows: string[] = []

        const defaultDataTypeMap: { [roomTypeId: string]: keyof DayDataTypes } =
          {}

        // Later should be improved and not iterate all rows
        rows.forEach((row) => {
          if ('dayDataType' in row && row.dayDataType !== 'price') {
            defaultDataTypeMap[row.data.roomTypeId] = row.dayDataType
          }
          if ('showCapacities' in row && row.showCapacities) {
            capacityOpenedRows.push(row.id)
          }
          if ('showChannels' in row && row.showChannels) {
            channelOpenedRows.push(row.id)
          }
        })

        Object.entries(loadedCapacities.current).forEach(
          ([fatherRowId, capacityRows]) => {
            capacityRows.forEach((capacityRow) => {
              const { capacityId: fatherCapacityId } =
                RatesDataGridContextUtils.getRowEntityIds(fatherRowId)
              const {
                data: { capacityId, hotelId, roomTypeId, rateId }
              } = capacityRow
              childrenCapacityHotelIds.add(hotelId)
              childrenCapacityRoomTypeIds.add(roomTypeId)
              childrenCapacityRateIds.add(rateId)
              loadedChildrenCapacityIds.add(capacityId)
              loadedChildrenCapacityIds.add(fatherCapacityId)
            })
          }
        )

        Object.entries(loadedChannels.current).forEach(
          ([fatherRowId, channelRows]) => {
            channelRows.forEach((channelRow) => {
              const {
                data: { hotelId, roomTypeId, rateId, capacityId }
              } = channelRow
              const { capacityId: fatherCapacityId } =
                RatesDataGridContextUtils.getRowEntityIds(fatherRowId)
              childrenChannelHotelIds.add(hotelId)
              childrenChannelRoomTypeIds.add(roomTypeId)
              childrenChannelRateIds.add(rateId)
              childrenChannelCapacityIds.add(capacityId)
              childrenChannelCapacityIds.add(fatherCapacityId)
            })
          }
        )

        const apiData = await fetchData({
          hotelList: checkedHotelIds,
          roomTypeList: checkedRoomTypeIds,
          rateList: filterRateId ? [filterRateId] : undefined,
          filterVisibilityId,
          controller: abortController
        })

        await Promise.all([
          (async () => {
            if (loadedChildrenCapacityIds.size > 0) {
              const childrenCapacityData = await fetchData({
                hotelList: Array.from(childrenCapacityHotelIds),
                roomTypeList: Array.from(childrenCapacityRoomTypeIds),
                rateList: Array.from(childrenCapacityRateIds),
                roomTypeCapacityList: Array.from(loadedChildrenCapacityIds),
                controller: abortController
              })

              const updatedCapacities: LoadedCapacityRows = {}

              RatesDataGridContextUtils.mapHotelDataListCapacityRows({
                hotelDataList: childrenCapacityData.hotelDataList,
                cb: (capacityRow) => {
                  const prevLoadedCapacities =
                    updatedCapacities[capacityRow.fatherId]
                  updatedCapacities[capacityRow.fatherId] = [
                    ...(prevLoadedCapacities ?? []),
                    {
                      ...capacityRow,
                      dayDataType:
                        defaultDataTypeMap[capacityRow.data.roomTypeId] ??
                        'price'
                    }
                  ]
                }
              })

              loadedCapacities.current = updatedCapacities
            }
          })(),
          (async () => {
            if (Object.keys(loadedChannels.current).length > 0) {
              const childrenChannelData = await fetchData({
                allChannels: true,
                hotelList: Array.from(childrenChannelHotelIds),
                roomTypeList: Array.from(childrenChannelRoomTypeIds),
                rateList: Array.from(childrenChannelRateIds),
                roomTypeCapacityList: Array.from(childrenChannelCapacityIds),
                controller: abortController
              })
              const updatedChannelRows: LoadedChannelRows = {}

              RatesDataGridContextUtils.mapHotelDataListChannelRows({
                hotelDataList: childrenChannelData.hotelDataList,
                cb: (channelRow) => {
                  const prevUpdatedChannelRows =
                    updatedChannelRows[channelRow.fatherId] ?? []
                  updatedChannelRows[channelRow.fatherId] = [
                    ...prevUpdatedChannelRows,
                    {
                      ...channelRow,
                      dayDataType:
                        defaultDataTypeMap[channelRow.data.roomTypeId] ??
                        'price'
                    }
                  ]
                }
              })

              loadedChannels.current = updatedChannelRows
            }
          })()
        ])

        const newRows = RatesDataGridContextUtils.hotelDataListToRows({
          hotelDataList: apiData.hotelDataList,
          defaultDayDataTypes: defaultDataTypeMap,
          loadedCapacityRows: Object.entries(loadedCapacities.current).reduce(
            (acc, [fatherRowId, capacityRows]) => {
              if (capacityOpenedRows.includes(fatherRowId))
                acc[fatherRowId] = capacityRows
              return acc
            },
            {} as LoadedCapacityRows
          ),
          loadedChannelRows: Object.entries(loadedChannels.current).reduce(
            (acc, [fatherRowId, channelRows]) => {
              if (channelOpenedRows.includes(fatherRowId)) {
                acc[fatherRowId] = channelRows
              }
              return acc
            },
            {} as LoadedChannelRows
          )
        })

        const daysList = dateUtils.getDateList(
          state.dateRange[0],
          state.dateRange[1]
        )

        dispatch({
          type: 'UPDATE_API_DATA',
          payload: {
            apiData,
            rows: newRows,
            daysList
          }
        })
        dispatch({
          type: 'SET_LOADING',
          payload: false
        })
        dispatch({
          type: 'EMPTY_EDITED_DATE',
          payload: undefined
        })
      } catch (error: any) {
        if (error.name !== 'CanceledError') {
          dispatch({
            type: 'SET_LOADING',
            payload: false
          })
          dispatch({
            type: 'EMPTY_EDITED_DATE',
            payload: undefined
          })
          console.log(error)
        }
      }
    },
    [
      treeItems,
      rows,
      fetchData,
      filterRateId,
      filterVisibilityId,
      state.dateRange
    ]
  )

  const initialCall = useCallback(async () => {
    try {
      dispatch({
        type: 'SET_LOADING',
        payload: true
      })

      const data = await fetchData()

      // const filterVisibilityIdToFetch = data.filterList[2].id

      const apiData = await fetchData({
        roomTypeList: data.roomTypeAuxList?.map(({ id }) => id)
        // filterVisibilityId: filterVisibilityIdToFetch
      })

      const selectedEnvironment = apiData.environmentDataAuxList.find(
        ({ id }) => id === environmentId
      )!

      const treeItems = hotelDataUtils.environmentListToTreeItem({
        defaultChecked: true,
        environmentDataAuxList: [selectedEnvironment],
        hotelAuxList: apiData.hotelAuxList ?? [],
        roomTypes: apiData.roomTypeAuxList ?? [],
        zoneAuxList: apiData.zoneAuxList ?? []
      })

      dispatch({
        type: 'SET_INITIAL_STATE',
        payload: {
          apiData,
          treeItems
          // filterVisibilityId: filterVisibilityIdToFetch
        }
      })
    } catch (error) {
      console.log(error)
    } finally {
      dispatch({
        type: 'SET_LOADING',
        payload: false
      })
      isInitialCall.current = false
    }
  }, [environmentId, fetchData])

  //  useEffects
  useEffect(() => {
    isInitialCall.current = true
    isInitialFilterCall.current = true
    initialCall()
    return () => {
      loadedCapacities.current = {}

      // Abort all loading capacities
      Object.values(capacityLoaderControllers.current).forEach(
        (capacityLoaderController) => capacityLoaderController.abort()
      )
      capacityLoaderControllers.current = {}

      // Abort all loading channels
      Object.values(channelLoaderControllers.current).forEach(
        (channelLoaderController) => channelLoaderController.abort()
      )
      channelLoaderControllers.current = {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentId])

  useEffect(() => {
    const asyncFunc = async () => {
      if (isInitialCall.current) {
        return
      }
      if (isInitialFilterCall.current) {
        isInitialFilterCall.current = false
        return
      }
      const newController = abortAndReplace()
      await updateData({ abortController: newController })
    }

    asyncFunc()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeItems, filterVisibilityId, filterRateId, dateRange])

  // Methods

  const refreshData = useCallback(async () => {
    return await updateData({
      abortController: fetchDataController
    })
  }, [fetchDataController, updateData])

  const changeRateRowDataType = useCallback(
    ({
      row,
      dataType
    }: RatesDataGridContextActionTypes['CHANGE_RATE_ROW_DATA_TYPE']) => {
      // changeLoadedCapacities
      loadedCapacities.current = Object.entries(
        loadedCapacities.current
      ).reduce((acc, [fatherId, capacityRows]) => {
        const { roomTypeId } =
          RatesDataGridContextUtils.getRowEntityIds(fatherId)
        if (roomTypeId === row.data.roomTypeId) {
          acc[fatherId] = capacityRows.map((capacityRow) => ({
            ...capacityRow,
            dayDataType: dataType
          }))
        } else {
          acc[fatherId] = capacityRows
        }
        return acc
      }, {} as LoadedCapacityRows)

      // changeLoadedChannels
      loadedChannels.current = Object.entries(loadedChannels.current).reduce(
        (acc, [fatherId, channelRows]) => {
          const { roomTypeId } =
            RatesDataGridContextUtils.getRowEntityIds(fatherId)
          if (roomTypeId === row.data.roomTypeId) {
            acc[fatherId] = channelRows.map((channelRow) => ({
              ...channelRow,
              dayDataType: dataType
            }))
          } else {
            acc[fatherId] = channelRows
          }
          return acc
        },
        {} as LoadedChannelRows
      )

      dispatch({
        type: 'CHANGE_RATE_ROW_DATA_TYPE',
        payload: {
          row,
          dataType
        }
      })
    },
    []
  )

  const openRateCapacityRows = useCallback(
    async ({ row, rowIndex }: { row: RateRow; rowIndex: number }) => {
      let rowIndexToAdd = rowIndex
      let nextRowIndex = rowIndex + 1

      while (
        rows[nextRowIndex] &&
        (rows[nextRowIndex].type === RowType.CHANNEL ||
          rows[nextRowIndex].type === RowType.CHANNEL_SKELETON)
      ) {
        rowIndexToAdd++
        nextRowIndex++
      }

      try {
        dispatch({
          type: 'OPEN_RATE_ROW',
          payload: {
            rowIndex,
            type: RowType.CAPACITY
          }
        })
        if (loadedCapacities.current[row.id]) {
          dispatch({
            type: 'ADD_ROWS',
            payload: {
              rows: loadedCapacities.current[row.id],
              index: rowIndexToAdd
            }
          })
        } else {
          const skeletonRows: SkeletonRow[] = []
          for (let i = 0; i < row.capacitiesQty; i++) {
            skeletonRows.push({
              fatherId: row.id,
              id: generateUUID(),
              type: RowType.CAPACITY_SKELETON
            })
          }
          dispatch({
            type: 'ADD_ROWS',
            payload: {
              rows: skeletonRows,
              index: rowIndexToAdd
            }
          })

          const abortController = new AbortController()

          capacityLoaderControllers.current[row.id] = abortController

          const response = await fetchData({
            hotelList: [row.data.hotelId],
            roomTypeList: [row.data.roomTypeId],
            rateList: [row.data.rateId],
            roomTypeCapacityList: row.data.rateData.roomTypeCapacityList,
            controller: abortController
          })

          const hotelData = response.hotelDataList[0]

          const roomTypeData = hotelData.roomTypeDataList[0]

          const rateData = roomTypeData.rateDataList[0]

          const capacityRows = rateData.roomTypeCapacityDataList
            .slice(1)
            .map((roomTypeCapacity) =>
              RatesDataGridContextUtils.capacityToRow({
                capacityData: roomTypeCapacity,
                fatherId: row.id,
                hotelId: hotelData.id,
                rate: row.data.rateData,
                roomTypeId: roomTypeData.id,
                defaultDayDataType: row.dayDataType
              })
            )

          loadedCapacities.current[row.id] = capacityRows

          while (
            rows[nextRowIndex] &&
            (rows[nextRowIndex].type === RowType.CHANNEL ||
              rows[nextRowIndex].type === RowType.CHANNEL_SKELETON)
          ) {
            rowIndexToAdd++
            nextRowIndex++
          }

          dispatch({
            type: 'UPDATE_ROWS',
            payload: {
              rowIndex: rowIndexToAdd + 1,
              rows: capacityRows,
              rowsReplaceQty: row.capacitiesQty,
              isCapacityRows: true
            }
          })
        }
      } catch (error) {
        console.log(error)
      } finally {
        delete capacityLoaderControllers.current[row.id]
      }
    },
    [fetchData, rows]
  )

  const closeRateCapacityRows = useCallback(
    ({ index, row }: { row: RateRow; index: number }) => {
      dispatch({
        type: 'CLOSE_RATE_ROW',
        payload: {
          index,
          type: RowType.CAPACITY
        }
      })

      let rowIndexToRemove = index + 1
      let nextRow = rows[rowIndexToRemove]

      while (
        nextRow.type === RowType.CHANNEL ||
        nextRow.type === RowType.CHANNEL_SKELETON
      ) {
        nextRow = rows[rowIndexToRemove + 1]
        rowIndexToRemove++
      }

      let rowsToRemove = row.capacitiesQty

      for (let i = rowIndexToRemove; i < rowIndexToRemove + rowsToRemove; i++) {
        const capacityRow = rows[i]
        if ('showChannels' in capacityRow && capacityRow.showChannels)
          rowsToRemove = rowsToRemove + capacityRow.channelsQty
      }

      dispatch({
        type: 'REMOVE_ROWS',
        payload: {
          rowIndex: rowIndexToRemove,
          rowsToRemove
        }
      })
      capacityLoaderControllers.current[row.id]?.abort()
    },
    [rows]
  )

  const openRateChannelRows = useCallback(
    async ({ index, row }: { index: number; row: RateRow | CapacityRow }) => {
      try {
        // const rateRow = rows[index] as RateRow
        dispatch({
          type: 'OPEN_RATE_ROW',
          payload: { rowIndex: index, type: RowType.CHANNEL }
        })

        const loadedRowChannels = loadedChannels.current[row.id]

        if (loadedRowChannels) {
          dispatch({
            type: 'ADD_ROWS',
            payload: {
              rows: loadedChannels.current[row.id],
              index
            }
          })
        } else {
          const channelSkeletonRows: SkeletonRow[] = []

          for (let i = 0; i < row.channelsQty; i++) {
            channelSkeletonRows.push({
              fatherId: row.id,
              id: generateUUID(),
              type: RowType.CHANNEL_SKELETON
            })
          }

          dispatch({
            type: 'ADD_ROWS',
            payload: {
              rows: channelSkeletonRows,
              index
            }
          })

          const channelAbortController = new AbortController()

          channelLoaderControllers.current[row.id] = channelAbortController

          const response = await fetchData({
            hotelList: [row.data.hotelId],
            roomTypeList: [row.data.roomTypeId],
            rateList: [row.data.rateId],
            roomTypeCapacityList: [row.data.capacityId],
            allChannels: true,
            controller: channelAbortController
          })

          const hotel = response.hotelDataList[0]
          const roomType = hotel.roomTypeDataList[0]
          const rate = roomType.rateDataList[0]
          const capacity = rate.roomTypeCapacityDataList[0]

          const channelRows = capacity.channelDataList
            .slice(1)
            .map((channel): ChannelRow => {
              return RatesDataGridContextUtils.channelToRow({
                channelData: channel,
                capacityId: capacity.id,
                fatherId: row.id,
                hotelId: hotel.id,
                rateId: row.data.rateId,
                roomTypeId: roomType.id,
                defaultDayDataType: row.dayDataType
              })
            })

          loadedChannels.current[row.id] = channelRows

          dispatch({
            type: 'UPDATE_ROWS',
            payload: {
              rowIndex: index + 1,
              rows: channelRows,
              rowsReplaceQty: row.channelsQty
            }
          })
        }
      } catch (error) {
        console.log(error)
      } finally {
        delete channelLoaderControllers.current[row.id]
      }
    },
    [fetchData]
  )

  const closeRateChannelRows = useCallback(
    ({ index, row }: { index: number; row: CapacityRow | RateRow }) => {
      channelLoaderControllers.current[row.id]?.abort()
      dispatch({
        type: 'CLOSE_RATE_ROW',
        payload: {
          index,
          type: RowType.CHANNEL
        }
      })
      dispatch({
        type: 'REMOVE_ROWS',
        payload: {
          rowIndex: index + 1,
          rowsToRemove:
            loadedChannels.current[row.id]?.length ?? row.channelsQty
        }
      })
    },
    []
  )

  const changeDateRange = useCallback((newDateRange: [Date, Date]) => {
    dispatch({
      type: 'SET_DATE_RANGE',
      payload: newDateRange
    })
  }, [])

  // Filter methods

  const changeTreeItems = useCallback((newTreeItems: TreeItemI[]) => {
    dispatch({
      type: 'SET_TREE_ITEMS',
      payload: newTreeItems
    })
  }, [])

  const changeFilterRateId = useCallback((rateId: string) => {
    dispatch({
      type: 'SET_FILTER_RATE_ID',
      payload: rateId
    })
  }, [])

  const changeFilterVisibility = useCallback((visibilityId: string) => {
    dispatch({
      type: 'SET_FILTER_VISIBILITY_ID',
      payload: visibilityId
    })
  }, [])

  const resetFilters = useCallback(() => {
    dispatch({
      type: 'RESET_FILTERS',
      payload: undefined
    })
  }, [])

  // Edit Date Data

  const setEditDateData = useCallback(
    ({
      date,
      dayData,
      row
    }: {
      date: Date
      dayData: Partial<DayDataTypes>
      row: RateRow | ChannelRow | CapacityRow
    }) => {
      dispatch({
        type: 'SET_EDITED_DATE',
        payload: {
          date,
          dayData,
          row
        }
      })
    },
    []
  )

  const openMassiveModModal = useCallback(
    (row?: RateRow | ChannelRow | CapacityRow) => {
      dispatch({
        type: 'SET_MASSIVE_MOD_MODAL',
        payload: row || true
      })
    },
    []
  )

  const closeMassiveModModal = useCallback(() => {
    dispatch({
      type: 'SET_MASSIVE_MOD_MODAL',
      payload: false
    })
  }, [])

  const handleSaveEditedData = useCallback(async () => {
    try {
      const dateDataList: ApiSaveDataDateDataList[] = []
      dispatch({ type: 'SET_LOADING', payload: true })
      Object.entries(editedDaysList).forEach(([rowId, dateValueMap]) => {
        Object.entries(dateValueMap).forEach(([date, editedValue]) => {
          const { rateId, channelId, roomTypeId, capacityId } =
            RatesDataGridContextUtils.getRowEntityIds(rowId)
          const editedDate = date
          dateDataList.push({
            channelList: [channelId],
            iniDate: editedDate,
            endDate: editedDate,
            rateList: [rateId],
            roomTypeList: [roomTypeId],
            roomTypeCapacityId: capacityId,
            ...editedValue
          })
        })
      })
      await apiSaveData({
        serverPath: serverUrl,
        data: {
          environmentDataId: environmentId!,
          userToken: token,
          dateDataList
        }
      })
      toast.success({ message: t('alerts.data_saved') })
    } catch (error) {
      toast.error({
        message: t('alerts.An_error_has_ocurred_try_again')
      })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [editedDaysList, environmentId, serverUrl, toast, token])

  const saveEditedData = useCallback(async () => {
    await handleSaveEditedData()
  }, [handleSaveEditedData])

  const providerValue: RatesDataGridContextProps = useMemo(
    () => ({
      ...state,
      apiData,
      changeRateRowDataType,
      changeFilterVisibility,
      changeTreeItems,
      changeFilterRateId,
      changeDateRange,
      openRateChannelRows,
      closeRateChannelRows,
      openRateCapacityRows,
      closeRateCapacityRows,
      resetFilters,
      setEditDateData,
      closeMassiveModModal,
      openMassiveModModal,
      saveEditedData,
      refreshData,
      capacityLoaderControllers,
      channelLoaderControllers
    }),
    [
      apiData,
      changeDateRange,
      changeFilterRateId,
      changeFilterVisibility,
      changeRateRowDataType,
      changeTreeItems,
      closeMassiveModModal,
      closeRateCapacityRows,
      closeRateChannelRows,
      saveEditedData,
      openMassiveModModal,
      openRateCapacityRows,
      openRateChannelRows,
      refreshData,
      resetFilters,
      setEditDateData,
      state
    ]
  )

  return (
    <RatesDataGridContext.Provider value={providerValue}>
      {children}
    </RatesDataGridContext.Provider>
  )
}
export default RatesDataGridContextProvider

export const useRatesDataGridContext = () => useContext(RatesDataGridContext)
